import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CreditCard, Ticket, ArrowRight } from 'lucide-react';

/**
 * Public branded landing page for the client domain (app.travcations.com root).
 * Three self-service entry points only — no agent UI, no login.
 *   View My Itinerary → /{ITI ref}/preview
 *   Pay for My Trip   → /{TRP ref}/pay
 *   View My Voucher   → /{ITI ref}/voucher
 */
const TEAL = '#0F6E56';

const EntryCard = ({ icon: Icon, title, placeholder, cta, onGo, testid }) => {
  const [ref, setRef] = useState('');
  const [err, setErr] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    const v = ref.trim();
    if (!v) { setErr(true); return; }
    onGo(v);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`landing-card-${testid}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: TEAL }}>
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
      </div>
      <input
        type="text"
        value={ref}
        onChange={(e) => { setRef(e.target.value); setErr(false); }}
        placeholder={placeholder}
        data-testid={`landing-input-${testid}`}
        className={`w-full h-12 rounded-xl border px-4 text-[15px] outline-none focus:ring-2 focus:ring-[#0F6E56]/30 ${err ? 'border-rose-400' : 'border-slate-200'}`}
      />
      {err && <p className="text-[12px] text-rose-600 mt-1.5" data-testid={`landing-err-${testid}`}>Please enter your reference number.</p>}
      <button
        type="submit"
        data-testid={`landing-go-${testid}`}
        className="mt-3 w-full h-12 rounded-xl text-white text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
        style={{ backgroundColor: TEAL }}
      >
        {cta} <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
};

export default function ClientLanding() {
  const navigate = useNavigate();
  const enc = (v) => encodeURIComponent(v.trim());

  return (
    <div className="min-h-screen bg-white px-5 py-10 flex flex-col items-center" data-testid="client-landing">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center text-center mb-8">
          <img
            src="/api/uploads/travcations-logo.png"
            alt="Travcations"
            className="h-12 w-auto"
            onError={(e) => { e.target.style.display = 'none'; }}
            data-testid="landing-logo"
          />
          <p className="mt-4 text-[15px] text-slate-500" data-testid="landing-tagline">Your travel documents, anytime.</p>
        </div>

        {/* Entry points */}
        <div className="space-y-4">
          <EntryCard
            icon={FileText}
            title="View My Itinerary"
            placeholder="Itinerary ref (e.g. ITI123ABC)"
            cta="View itinerary"
            testid="itinerary"
            onGo={(v) => navigate(`/${enc(v)}/preview`)}
          />
          <EntryCard
            icon={CreditCard}
            title="Pay for My Trip"
            placeholder="Payment ref (e.g. TRP123ABC)"
            cta="Pay now"
            testid="pay"
            onGo={(v) => navigate(`/${enc(v)}/pay`)}
          />
          <EntryCard
            icon={Ticket}
            title="View My Voucher"
            placeholder="Itinerary ref (e.g. ITI123ABC)"
            cta="View voucher"
            testid="voucher"
            onGo={(v) => navigate(`/${enc(v)}/voucher`)}
          />
        </div>

        <p className="text-center text-[12px] text-slate-400 mt-10" data-testid="landing-footer">© Travcations · Let&apos;s Explore to Experience</p>
      </div>
    </div>
  );
}
